/**
 * WO-D2.M — account self-administration API (license, users, comms config).
 * Shapes come from @beam/api-types; routes confirmed in beam-backend/src/api/{licenseRouter,
 * accountRouter}.ts. Never imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */
import {
  LicenseStatusResponse,
  AccountUser,
  InviteUserRequest,
  MessagingIdentityInput,
  SecretDescriptor,
  SessionDto,
} from '@beam/api-types';
import type {
  LicenseStatusResponse as LicenseStatusResponseType,
  AccountUser as AccountUserType,
  InviteUserRequest as InviteUserRequestType,
  MessagingIdentityInput as MessagingIdentityInputType,
} from '@beam/api-types';
import { z } from 'zod';
import type { SessionDto as SessionDtoType } from '@beam/api-types';
import { get, post, put, patch, del } from './client';

// --- License ---
export async function getLicense(): Promise<LicenseStatusResponseType> {
  return LicenseStatusResponse.parse(await get<unknown>('/api/license'));
}

// --- Users ---
export async function listUsers(): Promise<AccountUserType[]> {
  return z.array(AccountUser).parse(await get<unknown>('/api/account/users'));
}
export async function inviteUser(input: InviteUserRequestType): Promise<{ userId: string; inviteToken: string }> {
  const body = InviteUserRequest.parse(input);
  return post('/api/account/users', body);
}
export async function setUserRole(id: string, role: string): Promise<unknown> {
  return patch(`/api/account/users/${id}/role`, { role });
}
export async function setUserActive(id: string, is_active: boolean): Promise<unknown> {
  return patch(`/api/account/users/${id}/active`, { is_active });
}

// --- Comms config ---
const CommsIdentity = z.object({
  id: z.string(),
  channel: z.string(),
  provider: z.string(),
  from: z.string(),
  config: z.unknown().optional(),
  secrets: z.array(SecretDescriptor),
});
export type CommsIdentity = z.infer<typeof CommsIdentity>;

export async function listIdentities(): Promise<CommsIdentity[]> {
  return z.array(CommsIdentity).parse(await get<unknown>('/api/account/comms/identities'));
}
export async function upsertIdentity(input: MessagingIdentityInputType): Promise<unknown> {
  return put('/api/account/comms/identities', MessagingIdentityInput.parse(input));
}
export async function setIdentitySecret(id: string, secret: string): Promise<unknown> {
  return post(`/api/account/comms/identities/${id}/secret`, { secret });
}
export async function getInboundAddress(): Promise<{ address: string | null; token: string | null }> {
  return get('/api/account/comms/inbound-address');
}
export async function rotateInboundAddress(): Promise<{ address: string }> {
  return post('/api/account/comms/inbound-address/rotate');
}

// --- Billing recovery (works even when locked) ---
export async function openBillingPortal(): Promise<{ url: string }> {
  return post('/api/billing/portal');
}

// --- Sessions & security (W1) ---
export async function listSessions(): Promise<SessionDtoType[]> {
  return z.array(SessionDto).parse(await get<unknown>('/api/auth/sessions'));
}
export async function revokeSession(id: string): Promise<void> {
  await del<unknown>(`/api/auth/sessions/${id}`);
}
export async function logoutAll(): Promise<{ revoked: number }> {
  return post('/api/auth/logout-all');
}
